import type { Signal } from "@inrev/signals";
import { SignalSchema } from "@inrev/signals";
import { SseSessionEndedError } from "@portal/api/server-sent-events/SseSessionEndedError";
import { createParser, type EventSourceMessage } from "eventsource-parser";
import { type RefObject, useEffect, useRef, useState } from "react";
import { match } from "ts-pattern";
import { drainEventStream } from "../../utils/drainEventStream";
import { AuthenticationError } from "../../utils/request";
import { logDataToSentry } from "../../utils/sentry";

const userAwayTimeout = 5 * 60 * 1000;
const tokenRefreshRetryDelay = 3000;
const unexpectedStatusReconnectDelay = 5000;
const maxRetries = 2;
const logFailureAt = 2;
const baseTransportReconnectDelay = 1000;
const maxTransportReconnectDelay = 30_000;
const maxReconnectJitterMs = 1_000;
const heartbeatsBeforeHealthy = 2;

function transportReconnectDelay(consecutiveFailures: number): number {
	const steps = Math.max(0, consecutiveFailures - 1);
	return Math.min(maxTransportReconnectDelay, baseTransportReconnectDelay * 2 ** steps);
}

function withJitter(delayMs: number): number {
	return delayMs + Math.floor(Math.random() * maxReconnectJitterMs);
}

function authErrorCode(error: unknown): string | undefined {
	if (typeof error !== "object" || error === null || !("error" in error)) return undefined;
	return typeof error.error === "string" ? error.error : undefined;
}

function tokenErrorMessage(error: unknown): string {
	if (error instanceof Error) return error.message;
	return authErrorCode(error) ?? String(error);
}

function parseSignal(eventData: string): Signal | null {
	let data: unknown;
	try {
		data = JSON.parse(eventData);
	} catch {
		return null;
	}
	const result = SignalSchema.safeParse(data);
	return result.success ? result.data : null;
}

function validateResponse(
	response: Response,
	retryCountRef: RefObject<number>,
	forceRefreshRef: RefObject<boolean>,
	reconnect: (delayMs: number) => void,
	setIsDisabled: (v: boolean) => void,
): boolean {
	return match(response)
		.with({ status: 503 }, () => {
			logDataToSentry("sse:disabled", { status: response.status });
			setIsDisabled(true);
			return false;
		})
		.with({ status: 404 }, () => {
			logDataToSentry("sse:not_found", { status: response.status });
			setIsDisabled(true);
			return false;
		})
		.with({ status: 403 }, () => {
			logDataToSentry("sse:forbidden", { status: response.status });
			setIsDisabled(true);
			return false;
		})
		.with({ status: 401 }, () => {
			retryCountRef.current++;
			if (retryCountRef.current >= maxRetries) {
				logDataToSentry("sse:unauthorized", {
					status: response.status,
					attempts: retryCountRef.current,
				});
				setIsDisabled(true);
				return false;
			}
			forceRefreshRef.current = true;
			reconnect(tokenRefreshRetryDelay);
			return false;
		})
		.with({ ok: false }, () => {
			logDataToSentry("sse:unexpected_status", { status: response.status });
			reconnect(unexpectedStatusReconnectDelay);
			return false;
		})
		.when(
			() => !response.headers.get("content-type")?.includes("text/event-stream"),
			() => {
				logDataToSentry("sse:invalid_content_type", {
					status: response.status,
					contentType: response.headers.get("content-type"),
				});
				setIsDisabled(true);
				return false;
			},
		)
		.otherwise(() => {
			retryCountRef.current = 0;
			return true;
		});
}

async function consumeStream(
	response: Response,
	abortSignal: AbortSignal,
	lastEventIdRef: { current: string | null },
	onInvalidate: (queryKeys: string[][] | null) => void,
	onStreamHealthy: () => void,
	onStreamError: (error: unknown, lastSignal: Signal | null) => void,
): Promise<void> {
	let lastSignal: Signal | null = null;
	let heartbeats = 0;
	const parser = createParser({
		onComment() {
			heartbeats += 1;
			if (heartbeats === heartbeatsBeforeHealthy) onStreamHealthy();
		},
		onEvent(event: EventSourceMessage) {
			const signal = parseSignal(event.data);
			if (!signal) {
				return;
			}

			onStreamHealthy();
			lastSignal = signal;
			if (event.id) {
				lastEventIdRef.current = event.id;
			}
			if (signal.type === "invalidate") {
				onInvalidate(signal.queryKeys);
			}
		},
	});

	if (!response.body) {
		logDataToSentry("sse:empty_body", { error: `${response.status} Response yielded no body` });
		return;
	}
	try {
		await drainEventStream(response.body, parser, abortSignal);
	} catch (err) {
		if (abortSignal.aborted) return;
		onStreamError(err, lastSignal);
	}
}

interface SSESubscriberProps {
	getConnection: (forceRefresh?: boolean) => Promise<{ url: string; headers?: HeadersInit }>;
	onInvalidate: (queryKeys: string[][] | null) => void;
}

export function SSESubscriber({ getConnection, onInvalidate }: SSESubscriberProps) {
	const callbacksRef = useRef({ onInvalidate });
	const lastEventIdRef = useRef<string | null>(null);
	const retryCountRef = useRef(0);
	const forceRefreshRef = useRef(false);
	const transportFailureRef = useRef(0);
	const tokenFailureRef = useRef(0);
	const tokenFailureLoggedRef = useRef(false);
	callbacksRef.current = { onInvalidate };

	const [tick, setTick] = useState(0);
	const [isDisabled, setIsDisabled] = useState(false);
	const [isVisible, setIsVisible] = useState(!document.hidden);
	const hiddenAtRef = useRef<number | null>(null);

	async function fetchSSE(
		connection: { url: string; headers?: HeadersInit },
		signal: AbortSignal,
		reconnect: (delayMs: number) => void,
	): Promise<Response | null> {
		let response: Response;
		try {
			response = await fetch(connection.url, {
				headers: {
					...connection.headers,
					Accept: "text/event-stream",
					"Cache-Control": "no-cache",
					...(lastEventIdRef.current ? { "Last-Event-ID": lastEventIdRef.current } : {}),
				},
				signal,
			});
		} catch (err) {
			if (signal.aborted) return null;
			recordTransportFailure("sse:connection_error", {
				error: err instanceof Error ? err.message : String(err),
			});
			reconnect(transportReconnectDelay(transportFailureRef.current));
			return null;
		}
		const valid = validateResponse(
			response,
			retryCountRef,
			forceRefreshRef,
			reconnect,
			setIsDisabled,
		);
		return valid ? response : null;
	}

	function recordTransportFailure(
		name: "sse:connection_error" | "sse:stream_error",
		data: { error: string; lastSignal?: Signal | null },
	) {
		transportFailureRef.current += 1;
		if (transportFailureRef.current === logFailureAt) {
			logDataToSentry(name, data);
		}
	}

	useEffect(() => {
		const handle = () => {
			if (document.hidden) {
				hiddenAtRef.current = Date.now();
				setIsVisible(false);
			} else {
				if (hiddenAtRef.current !== null && Date.now() - hiddenAtRef.current > userAwayTimeout) {
					callbacksRef.current.onInvalidate(null);
				}
				hiddenAtRef.current = null;
				setIsVisible(true);
			}
		};
		document.addEventListener("visibilitychange", handle);
		return () => document.removeEventListener("visibilitychange", handle);
	}, []);

	useEffect(() => {
		if (isDisabled || !isVisible) return;

		const controller = new AbortController();
		const reconnect = (delayMs: number) => {
			if (!controller.signal.aborted) setTimeout(() => setTick((t) => t + 1), withJitter(delayMs));
		};

		async function run() {
			let connection: { url: string; headers?: HeadersInit };
			const forceRefresh = forceRefreshRef.current;
			forceRefreshRef.current = false;
			try {
				connection = await getConnection(forceRefresh);
				tokenFailureRef.current = 0;
				tokenFailureLoggedRef.current = false;
			} catch (err) {
				if (err instanceof SseSessionEndedError) {
					setIsDisabled(true);
					return;
				}
				tokenFailureRef.current += 1;
				const timeout = authErrorCode(err) === AuthenticationError.TIMEOUT;
				const tokenFailureConfirmed = !timeout || tokenFailureRef.current >= logFailureAt;
				if (!tokenFailureLoggedRef.current && tokenFailureConfirmed) {
					tokenFailureLoggedRef.current = true;
					logDataToSentry("sse:token_error", { error: tokenErrorMessage(err) });
				}
				reconnect(transportReconnectDelay(tokenFailureRef.current));
				return;
			}
			if (controller.signal.aborted) return;

			const response = await fetchSSE(connection, controller.signal, reconnect);
			if (!response) return;

			let streamFailed = false;
			await consumeStream(
				response,
				controller.signal,
				lastEventIdRef,
				(queryKey) => callbacksRef.current.onInvalidate(queryKey),
				() => {
					transportFailureRef.current = 0;
				},
				(err, lastSignal) => {
					streamFailed = true;
					recordTransportFailure("sse:stream_error", {
						error: err instanceof Error ? err.message : String(err),
						lastSignal,
					});
				},
			);
			if (!streamFailed) transportFailureRef.current = 0;

			reconnect(transportReconnectDelay(transportFailureRef.current));
		}

		run();
		return () => controller.abort();
	}, [isDisabled, isVisible, tick, getConnection]);

	return null;
}
